'use client';

import React, { ChangeEvent, forwardRef, useCallback, useId, useRef, useState } from 'react';
import { Icon } from '../../icons';
import { Caption } from '../Caption/Caption';
import './InputText.css';
import { AnimatePresence, motion, useAnimationControls } from 'motion/react';

export type TrailingAction = '' | 'copy' | 'clear' | 'custom' | 'password-toggle';

export interface InputTextProps extends Pick<React.InputHTMLAttributes<HTMLInputElement>,
  'role' | 'aria-label' | 'aria-controls' | 'aria-expanded' | 'aria-activedescendant'> {
  label?: string;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  placeholder?: string;
  type?: string;
  min?: number;
  max?: number;
  step?: number;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  value: string | number;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  leadingVisual?: string;
  trailingVisual?: string;
  trailingAction?: TrailingAction;
  onActionClicked?: (e: React.MouseEvent) => void;
  autoComplete?: string;
  caption?: React.ReactNode;
  message?: string;
  messageType?: '' | 'error' | 'success';
  id?: string;
  size?: 'sm' | 'md' | 'lg';
  /** If true, component stretches to 100% width */
  fullWidth?: boolean;
  /**
   * Optional custom trailing content rendered inside the input wrapper, after built-in visuals/actions.
   * Useful for embedding multiple small action icons.
   */
  trailingSlot?: React.ReactNode;
  /**
   * When trailingAction is 'custom', optionally override the icon name.
   */
  trailingActionIconName?: React.ComponentProps<typeof Icon>['name'];
  /**
   * Disable clicks on the trailing action without disabling the whole input.
   */
  trailingActionDisabled?: boolean;
  /**
   * Accessible label for the primary trailing action button.
   */
  trailingActionAriaLabel?: string;
  /**
   * Optional secondary trailing action icon and handler rendered after the primary trailing action.
   */
  secondaryTrailingActionIconName?: React.ComponentProps<typeof Icon>['name'];
  onSecondaryActionClicked?: (e: React.MouseEvent) => void;
  secondaryTrailingActionDisabled?: boolean;
  secondaryTrailingActionAriaLabel?: string;
  /** Disable Motion layout animations on the root wrapper */
  disableLayoutAnimation?: boolean;
}

export const InputText = React.memo(
  forwardRef<HTMLInputElement, InputTextProps>(function InputText(
    {
      label,
      required = false,
      disabled = false,
      readonly = false,
      placeholder = '',
      type = 'text',
      min,
      max,
      step,
      inputMode,
      value,
      onChange,
      onBlur,
      leadingVisual,
      trailingVisual,
      trailingAction = '',
      onActionClicked,
      autoComplete,
      caption,
      message,
      messageType = '',
      id,
      size = 'md',
      fullWidth = false,
      trailingSlot,
      trailingActionIconName,
      trailingActionDisabled = false,
      trailingActionAriaLabel,
      secondaryTrailingActionIconName,
      onSecondaryActionClicked,
      secondaryTrailingActionDisabled = false,
      secondaryTrailingActionAriaLabel,
      disableLayoutAnimation = false,
      ...inputAccessibility
    }: InputTextProps,
    ref,
  ) {
    const [copied, setCopied] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const customActionControls = useAnimationControls();
    const reactId = useId();
    const inputId = id ?? `input-text-${reactId}`;
    const captionId = caption ? `${inputId}-caption` : undefined;
    const messageId = message ? `${inputId}-message` : undefined;
    const inputRef = useRef<HTMLInputElement>(null);
    const setRefs = useCallback(
      (node: HTMLInputElement | null) => {
        inputRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
      },
      [ref],
    );

    const renderVisual = (visual: string, position: 'left' | 'right') => {
      return (
        <Icon
          name={visual as React.ComponentProps<typeof Icon>['name']}
          className={`input-text__icon input-text__icon--${position}`}
          aria-hidden="true"
        />
      );
    };

    const handleTrailingAction = (e: React.MouseEvent) => {
      // Allow copy even when the input itself is disabled
      const isDisabledAction = (disabled && trailingAction !== 'copy') || trailingActionDisabled;
      if (isDisabledAction) return;
      switch (trailingAction) {
        case 'copy':
          try {
            void navigator.clipboard.writeText(String(value));
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch {
            // ignore
          }
          break;
        case 'clear':
          onChange({ target: { value: '' } } as ChangeEvent<HTMLInputElement>);
          break;
        case 'password-toggle':
          setShowPassword((prev) => !prev);
          break;
        case 'custom':
          onActionClicked?.(e);
          break;
      }
    };

    let trailingActionIcon: React.ComponentProps<typeof Icon>['name'] | null = null;
    switch (trailingAction) {
      case 'clear':
        trailingActionIcon = 'XCircle';
        break;
      case 'copy':
        trailingActionIcon = copied ? 'Check' : 'Copy';
        break;
      case 'password-toggle':
        trailingActionIcon = showPassword ? 'EyeOff' : 'Eye';
        break;
      case 'custom':
        trailingActionIcon =
          (trailingActionIconName as React.ComponentProps<typeof Icon>['name']) ?? 'MoreHorizontal';
        break;
      default:
        trailingActionIcon = null;
    }

    const allowCopyWhenDisabled = disabled && trailingAction === 'copy';

    return (
      <motion.div
        className="input-text"
        data-disabled={disabled}
        data-allow-copy={allowCopyWhenDisabled || undefined}
        data-size={size}
        data-full-width={fullWidth || undefined}
        layout={!disableLayoutAnimation}
      >
        <label htmlFor={inputId}>
          {label && (
            <span className="input-text__label">
              {label}
              {required && <span className="input-text__required">*</span>}
            </span>
          )}
          {caption && (
            <Caption>
              <span id={captionId}>{caption}</span>
            </Caption>
          )}
          <motion.span
            className="input-text__wrapper"
            animate={{ x: messageType === 'error' ? [0, -6, 6, -4, 4, 0] : 0 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            style={{ willChange: 'transform' }}
          >
            {leadingVisual && renderVisual(leadingVisual, 'left')}
            {(() => {
              let effectiveAutoComplete: string | undefined = autoComplete;
              if (effectiveAutoComplete == null) {
                if (type === 'email') {
                  effectiveAutoComplete = 'email';
                } else if (trailingAction === 'password-toggle' || type === 'password') {
                  effectiveAutoComplete = 'current-password';
                }
              }

              return (
                <input
                  {...inputAccessibility}
                  id={inputId}
                  ref={setRefs}
                  type={
                    trailingAction === 'password-toggle'
                      ? showPassword
                        ? 'text'
                        : 'password'
                      : type
                  }
                  required={required}
                  readOnly={readonly}
                  disabled={disabled}
                  min={min}
                  max={max}
                  step={step}
                  inputMode={inputMode}
                  placeholder={placeholder}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  autoComplete={effectiveAutoComplete}
                  aria-invalid={messageType === 'error' || undefined}
                  aria-describedby={[captionId, messageId].filter(Boolean).join(' ') || undefined}
                />
              );
            })()}
            {trailingVisual && !trailingAction && renderVisual(trailingVisual, 'right')}
            {trailingAction && trailingActionIcon && (
              <motion.span
                className="input-text__trailing-action"
                onClick={handleTrailingAction}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleTrailingAction(e as unknown as React.MouseEvent);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-disabled={
                  (disabled && trailingAction !== 'copy') || trailingActionDisabled || undefined
                }
                aria-label={trailingActionAriaLabel ?? `${trailingAction} action`}
                whileTap={
                  trailingAction === 'copy' ||
                  trailingAction === 'password-toggle' ||
                  trailingAction === 'custom'
                    ? { scale: 0.85 }
                    : undefined
                }
                onTap={() => {
                  if (trailingAction === 'custom') {
                    void customActionControls.start({
                      scale: [1, 1.03, 1],
                      transition: { duration: 0.18, times: [0, 0.5, 1], ease: 'easeOut' },
                    });
                  }
                }}
                style={{ display: 'inline-flex', willChange: 'transform' }}
              >
                {trailingAction === 'copy' ? (
                  <span
                    style={{ position: 'relative', display: 'inline-flex', width: 20, height: 20 }}
                  >
                    <motion.span
                      initial={false}
                      animate={
                        copied
                          ? { opacity: 0, scale: 0.6 }
                          : { opacity: [0, 1, 1], scale: [0.8, 1.04, 1] }
                      }
                      transition={{ duration: 0.1, ease: 'easeOut', times: [0, 0.7, 1] }}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        willChange: 'transform, opacity',
                      }}
                    >
                      <Icon name="Copy" aria-hidden="true" />
                    </motion.span>
                    <motion.span
                      initial={false}
                      animate={
                        copied
                          ? { opacity: [0, 1, 1, 1], scale: [0.6, 1.12, 0.98, 1] }
                          : { opacity: [1, 0], scale: [1, 0.8] }
                      }
                      transition={
                        copied
                          ? { duration: 0.16, times: [0, 0.5, 0.8, 1], ease: 'easeOut' }
                          : { duration: 0.1, ease: 'easeOut' }
                      }
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        willChange: 'transform, opacity',
                      }}
                    >
                      <Icon name="Check" className="success" aria-hidden="true" />
                    </motion.span>
                  </span>
                ) : trailingAction === 'password-toggle' ? (
                  <span
                    style={{ position: 'relative', display: 'inline-flex', width: 20, height: 20 }}
                  >
                    {/* Eye (password hidden) */}
                    <motion.span
                      initial={false}
                      animate={
                        showPassword
                          ? { opacity: [1, 0], scale: [1, 0.6] }
                          : { opacity: [0, 1, 1], scale: [0.6, 1.12, 1] }
                      }
                      transition={
                        showPassword
                          ? { duration: 0.16, ease: 'easeOut' }
                          : { duration: 0.32, times: [0, 0.5, 1], ease: 'easeOut' }
                      }
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        willChange: 'transform, opacity',
                      }}
                    >
                      <Icon name="Eye" aria-hidden="true" />
                    </motion.span>
                    {/* EyeOff (password visible) */}
                    <motion.span
                      initial={false}
                      animate={
                        showPassword
                          ? { opacity: [0, 1, 1], scale: [0.6, 1.12, 1] }
                          : { opacity: [1, 0], scale: [1, 0.6] }
                      }
                      transition={
                        showPassword
                          ? { duration: 0.32, times: [0, 0.5, 1], ease: 'easeOut' }
                          : { duration: 0.16, ease: 'easeOut' }
                      }
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        willChange: 'transform, opacity',
                      }}
                    >
                      <Icon name="EyeOff" aria-hidden="true" />
                    </motion.span>
                  </span>
                ) : trailingAction === 'custom' ? (
                  <motion.span
                    animate={customActionControls}
                    initial={false}
                    style={{ display: 'inline-flex', willChange: 'transform' }}
                  >
                    <Icon name={trailingActionIcon} aria-hidden="true" />
                  </motion.span>
                ) : (
                  <motion.span
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.28, ease: 'easeOut' }}
                    style={{ display: 'inline-flex', willChange: 'transform' }}
                  >
                    <Icon
                      name={trailingActionIcon}
                      className={copied ? 'success' : ''}
                      aria-hidden="true"
                    />
                  </motion.span>
                )}
              </motion.span>
            )}
            {secondaryTrailingActionIconName && (
              <motion.span
                className="input-text__trailing-action"
                onClick={(e) => {
                  if (disabled || secondaryTrailingActionDisabled) return;
                  onSecondaryActionClicked?.(e);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (!(disabled || secondaryTrailingActionDisabled))
                      onSecondaryActionClicked?.(e as unknown as React.MouseEvent);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-disabled={disabled || secondaryTrailingActionDisabled || undefined}
                aria-label={secondaryTrailingActionAriaLabel ?? 'secondary action'}
                whileTap={{ scale: 0.85 }}
                style={{ display: 'inline-flex', willChange: 'transform' }}
              >
                <Icon
                  name={
                    secondaryTrailingActionIconName as React.ComponentProps<typeof Icon>['name']
                  }
                  aria-hidden="true"
                />
              </motion.span>
            )}
            {trailingSlot && <span className="input-text__trailing-slot">{trailingSlot}</span>}
          </motion.span>
        </label>
        <AnimatePresence initial={false}>
          {message && (
            <motion.div
              key="input-message"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              style={{ overflow: 'hidden' }}
            >
              <span id={messageId} className="input-text__message" data-type={messageType}>
                {message}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }),
);

InputText.displayName = 'InputText';
